import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';
import { ContactService, ContactMessage } from '../../services/contact.service';
import { AuthService, User } from '../../services/auth.service';

@Component({
  selector: 'app-contact-us',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    HeaderComponent,
    FooterComponent,
  ],
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.css'],
})
export class ContactUsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  contactForm: FormGroup;
  isSubmitting = false;
  isSubmitted = false;
  submitSuccess = false;
  submitMessage = '';
  showContactForm = false;
  currentUser: User | null = null;
  isLoggedIn = false;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private contactService: ContactService,
    private authService: AuthService
  ) {
    this.contactForm = this.createContactForm();
  }

  ngOnInit(): void {
    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
        this.isLoggedIn = !!user;

        if (user && user.email) {
          this.contactForm.patchValue({
            email: user.email,
          });
          this.contactForm.get('email')?.disable();
        } else {
          this.contactForm.get('email')?.enable();
          this.contactForm.patchValue({
            email: '',
          });
        }
      });

    this.authService.getCurrentUser().subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createContactForm(): FormGroup {
    return this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(/^[\+]?[1-9][\d]{0,15}$/)]],
      subject: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(200),
        ],
      ],
      message: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(5000),
        ],
      ],
    });
  }

  navigateWithScroll(route: string): void {
    this.router.navigateByUrl(route).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  navigateToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: '/contact-us' },
    });
  }

  navigateToRegister(): void {
    this.router.navigate(['/register'], {
      queryParams: { returnUrl: '/contact-us' },
    });
  }

  openEmailClient(): void {
    const emailParts = {
      user: 'support',
      domain: 'markado',
      tld: 'net',
    };
    const email = `${emailParts.user}@${emailParts.domain}.${emailParts.tld}`;
    window.location.href = `mailto:${email}?subject=Support Request`;
  }

  showForm(): void {
    if (!this.isLoggedIn) {
      this.navigateToLogin();
      return;
    }

    this.showContactForm = true;
    setTimeout(() => {
      const formElement = document.getElementById('contact-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  hideForm(): void {
    this.showContactForm = false;
    this.resetForm();
  }

  onSubmit(): void {
    if (!this.isLoggedIn) {
      this.navigateToLogin();
      return;
    }

    if (this.contactForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.submitMessage = '';

      const emailValue =
        this.contactForm.get('email')?.value || this.currentUser?.email;

      const contactData: ContactMessage = {
        name: this.contactForm.value.name.trim(),
        email: emailValue.trim().toLowerCase(),
        subject: this.contactForm.value.subject.trim(),
        message: this.contactForm.value.message.trim(),
        phone: this.contactForm.value.phone?.trim() || undefined,
      };

      this.contactService.sendContactMessage(contactData).subscribe({
        next: (response) => {
          this.submitSuccess = true;
          this.submitMessage =
            response.message || 'Your message has been sent successfully!';
          this.isSubmitted = true;
          this.resetFormFields();

          setTimeout(() => {
            this.hideForm();
            this.resetSubmissionState();
          }, 8000);
        },
        error: (error) => {
          this.submitSuccess = false;
          this.submitMessage =
            error.message || 'Failed to send message. Please try again.';
          this.isSubmitted = true;

          setTimeout(() => {
            this.isSubmitted = false;
            this.submitMessage = '';
          }, 6000);
        },
        complete: () => {
          this.isSubmitting = false;
        },
      });
    } else {
      Object.keys(this.contactForm.controls).forEach((key) => {
        this.contactForm.get(key)?.markAsTouched();
      });
    }
  }

  resetForm(): void {
    this.resetFormFields();
    this.resetSubmissionState();
  }

  private resetFormFields(): void {
    this.contactForm.patchValue({
      name: '',
      phone: '',
      subject: '',
      message: '',
    });

    Object.keys(this.contactForm.controls).forEach((key) => {
      const control = this.contactForm.get(key);
      if (control && key !== 'email') {
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
  }

  private resetSubmissionState(): void {
    this.isSubmitted = false;
    this.submitSuccess = false;
    this.submitMessage = '';
    this.isSubmitting = false;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contactForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.contactForm.get(fieldName);
    if (!field || !field.errors) return '';

    const errors = field.errors;

    if (errors['required'])
      return `${this.getFieldDisplayName(fieldName)} is required`;
    if (errors['email']) return 'Please enter a valid email address';
    if (errors['minlength'])
      return `${this.getFieldDisplayName(fieldName)} is too short`;
    if (errors['maxlength'])
      return `${this.getFieldDisplayName(fieldName)} is too long`;
    if (errors['pattern']) {
      if (fieldName === 'phone') return 'Please enter a valid phone number';
    }

    return 'Invalid input';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Subject',
      message: 'Message',
    };
    return displayNames[fieldName] || fieldName;
  }

  getCharacterCount(fieldName: string): number {
    const field = this.contactForm.get(fieldName);
    return field?.value?.length || 0;
  }
}
